import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import {
  allEmployeesRuleGroups,
  isAllEmployeesRuleGroups,
} from "@/components/apply-to/rule-grouping.ts";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  FontAwesomeIcon,
  Input,
  Skeleton,
  Stack,
  Switch,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { RoleEditTabs } from "@/pages/settings/roles/edit/components/RoleEditTabs.tsx";
import {
  EditRoleSchema,
  useRoleEditSchema,
} from "@/pages/settings/roles/edit/use-role-edit-schema.hook.ts";
import { getRoleDescription, getRoleTitle, mockRole } from "@/pages/settings/roles/role.types.ts";
import { useRoleById, useRolesDelete, useRolesSave } from "@/queries/settings/use-roles.query.ts";
import { useAllTables, useSectionFields } from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFieldTypes, ApiCFEntityTypes, ApiTableTypes } from "@/types/custom-field.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { ApiRoleSavePayload, PermissionType, PrincipalType, RoleType } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { RuleGroup } from "@/types/rule.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { ServiceUserAssign } from "@/pages/settings/roles/edit/components/ServiceUserAssign.tsx";
import {
  assignToToServiceUserEntities,
  serviceUserEntitiesToAssignTo,
} from "@/pages/settings/roles/edit/components/service-user-assign.mappers.ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useDocumentFoldersList } from "@/queries/use-documents.query.ts";
import { ApiFolderType } from "@/types/document.types.ts";
import { useWorkflowsShared } from "@/queries/workflows/workflow.query.ts";
import {
  getRolePermissionTables,
  getSystemTableBasePermissions,
  getSystemTableFieldPermission,
  systemTableManagePermissionFields,
} from "@/pages/settings/roles/edit/role-edit-service.ts";

export function RolesEdit() {
  const { t } = useTranslation();
  const assignToConditionTypes = useRuleConditionTypes(RuleRegistryPlace.roleAssignTo);
  const applyToConditionTypes = useRuleConditionTypes(RuleRegistryPlace.roleApplyTo);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { isFeatureEnabled } = usePermissions();
  const modalsService = useModals();
  const navigate = useNavigate();
  const { data: role, isLoading } = useRoleById(id ? parseInt(id, 10) : undefined);

  const principalType =
    role?.principal_type ??
    (searchParams.get("principal_type") === PrincipalType.api_user
      ? PrincipalType.api_user
      : PrincipalType.employee);
  const isApiUserRole = principalType === PrincipalType.api_user;

  const isCustomFieldsEnabled = isFeatureEnabled(ApiFeatureType.custom_fields);
  const { data: fields } = useSectionFields(ApiCFEntityTypes.employee, isCustomFieldsEnabled);
  const { data: allTables } = useAllTables(isCustomFieldsEnabled);
  const permissionTables = useMemo(() => getRolePermissionTables(allTables), [allTables]);
  const { data: folders } = useDocumentFoldersList(ApiFolderType.employee);
  const isWorkflowsEnabled = isFeatureEnabled(ApiFeatureType.workflows);
  const { data: workflows } = useWorkflowsShared(isWorkflowsEnabled);

  useTitle(role ? getRoleTitle(role) : undefined, t("common:edit"), t("core:role"));

  const formConfigurationSchemaObject = useRoleEditSchema(
    fields?.filter((field) => field.type == ApiFieldTypes.custom),
    folders,
    workflows
  );

  type formConfigurationSchemaInput = z.input<typeof formConfigurationSchemaObject>;
  type formConfigurationSchema = z.output<typeof formConfigurationSchemaObject>;

  const formHook = useForm<formConfigurationSchemaInput, unknown, formConfigurationSchema>({
    mode: "onSubmit",
    resolver: zodResolver(formConfigurationSchemaObject),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = formHook;

  const [accessAllEmployees, setAccessAllEmployees] = useState(false);
  const assignToGroups = useWatch({ control, name: "assign_to" }) as RuleGroup[] | undefined;

  const dataAccessActive = !accessAllEmployees;

  const roleSave = useRolesSave(control);
  const roleDelete = useRolesDelete();

  useEffect(() => {
    const defaultCustomFields: Record<string, PermissionType> = {};

    if (fields) {
      fields
        .filter((field) => field.type == ApiFieldTypes.custom)
        .forEach((field) => {
          defaultCustomFields[field.slug] = PermissionType.none;
        });
    }

    const defaultFolderPermissions: Record<number, PermissionType> = {};
    if (folders) {
      folders.forEach((folder) => {
        defaultFolderPermissions[folder.id] = PermissionType.none;
      });
    }

    const defaultWorkflowPermissions: Record<
      number,
      { run: PermissionType; executions: PermissionType }
    > = {};
    if (workflows) {
      workflows.forEach((workflow) => {
        defaultWorkflowPermissions[workflow.id] = {
          run: PermissionType.none,
          executions: PermissionType.none,
        };
      });
    }

    const defaultCustomTablePermissions: EditRoleSchema["employee_custom_table_field_permissions"] =
      {};
    if (permissionTables) {
      permissionTables.forEach((table) => {
        defaultCustomTablePermissions[table.id] = {
          history: PermissionType.none,
          manage_table: PermissionType.none,
          effective_from: PermissionType.none,
          changed_by: PermissionType.none,
          fields_permissions: table.table_fields.reduce(
            (acc, field) => ({ ...acc, [field.slug]: PermissionType.none }),
            {} as Record<string, PermissionType>
          ),
        };
      });
    }

    if (role) {
      const apiPermissions = { ...mockRole.api_permissions, ...role.api_permissions };
      const employeeFieldPermissions = {
        ...mockRole.employee_field_permissions,
        ...role.employee_field_permissions,
      };

      const roleCustomFieldsPermissions: Record<string, PermissionType> = {};
      role.employee_custom_field_permissions.forEach((permission) => {
        roleCustomFieldsPermissions[permission.slug] = permission.permission_value;
      });

      const roleFolderPermissions: Record<number, PermissionType> = {};
      role.employee_folder_permissions.forEach((permission) => {
        roleFolderPermissions[permission.folder_id] = permission.permission_value;
      });

      const roleWorkflowPermissions: Record<
        number,
        { run: PermissionType; executions: PermissionType }
      > = {};
      role.employee_workflow_permissions.forEach((permission) => {
        roleWorkflowPermissions[permission.workflow_id] = {
          run: permission.run,
          executions: permission.executions,
        };
      });

      const roleCustomTableFieldPermissions = role.employee_custom_table_permissions.reduce<
        EditRoleSchema["employee_custom_table_field_permissions"]
      >(
        (acc, table) => ({
          ...acc,
          [table.table_id]: {
            history: table.history,
            manage_table: table.manage_table,
            effective_from: table.effective_from,
            changed_by: table.changed_by,
            fields_permissions: table.field_permissions.reduce(
              (fieldAcc, field) => ({ ...fieldAcc, [field.slug]: field.permission_value }),
              {} as Record<string, PermissionType>
            ),
          },
        }),
        {}
      );

      const employeeCustomFieldsPermissions = {
        ...defaultCustomFields,
        ...roleCustomFieldsPermissions,
      };

      const employeeFolderPermissions = {
        ...defaultFolderPermissions,
        ...roleFolderPermissions,
      };

      const employeeWorkflowPermissions = {
        ...defaultWorkflowPermissions,
        ...roleWorkflowPermissions,
      };

      const employeeCustomTableFieldPermissions = {
        ...defaultCustomTablePermissions,
        ...roleCustomTableFieldPermissions,
      };

      permissionTables.forEach((table) => {
        if (table.table_type === ApiTableTypes.custom) return;

        const tablePermissions = employeeCustomTableFieldPermissions[table.id];
        if (!tablePermissions) return;

        tablePermissions.fields_permissions = Object.fromEntries(
          Object.entries(tablePermissions.fields_permissions || {}).map(([slug, permission]) => [
            slug,
            permission === PermissionType.edit ? PermissionType.view : permission,
          ])
        );
      });

      reset({
        ...role,
        title: getRoleTitle(role),
        description: getRoleDescription(role),
        api_user_entities:
          role.principal_type === PrincipalType.api_user
            ? assignToToServiceUserEntities(role.assign_to)
            : [],
        api_permissions: apiPermissions,
        employee_field_permissions: employeeFieldPermissions,
        employee_custom_field_permissions: employeeCustomFieldsPermissions,
        employee_folder_permissions: employeeFolderPermissions,
        employee_workflow_permissions: employeeWorkflowPermissions,
        employee_custom_table_field_permissions: employeeCustomTableFieldPermissions,
      });

      // eslint-disable-next-line react-hooks/set-state-in-effect -- the toggle mounts the apply_to editor, which must read the form after reset
      setAccessAllEmployees(isAllEmployeesRuleGroups(role.apply_to));

      return;
    }

    reset({
      ...mockRole,
      principal_type: principalType,
      api_user_entities: [],
      employee_custom_field_permissions: defaultCustomFields,
      employee_folder_permissions: defaultFolderPermissions,
      employee_workflow_permissions: defaultWorkflowPermissions,
      employee_custom_table_field_permissions: defaultCustomTablePermissions,
    });

    setAccessAllEmployees(false);
  }, [role, fields, permissionTables, folders, workflows, principalType]);

  const handleDelete = useCallback(() => {
    if (!role) return;
    if (roleSave.isPending) return;

    modalsService.openModal(ConfirmationModal, {
      question: t("common:delete"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await roleDelete.mutateAsync(role.id);

          analyticsService.trackEvent(analyticEvents.roles.deleted, {
            [analyticProperties.id]: role.id,
          });

          navigate("/settings/roles-and-permissions");
        } catch {
          return;
        }
      },
    });
  }, [role]);

  const handleCancel = () => {
    navigate("/settings/roles-and-permissions");
  };

  const onSubmit = handleSubmit((roleData) => {
    if (roleSave.isPending) return;

    const employeeCustomTablePermissions = Object.entries(
      roleData.employee_custom_table_field_permissions || {}
    ).map(([tableId, tableData]) => {
      const table = permissionTables.find((item) => item.id === Number(tableId));
      const managePermissionField = table
        ? systemTableManagePermissionFields[table.table_type]
        : undefined;
      const managePermission = managePermissionField
        ? roleData.employee_field_permissions[managePermissionField]
        : undefined;
      const systemTablePermissions = table
        ? getSystemTableBasePermissions(table.table_type, roleData.employee_field_permissions)
        : null;

      return {
        table_id: Number(tableId),
        history: systemTablePermissions?.history ?? tableData.history,
        manage_table: systemTablePermissions?.manage_table ?? tableData.manage_table,
        effective_from: systemTablePermissions?.effective_from ?? tableData.effective_from,
        changed_by: systemTablePermissions?.changed_by ?? tableData.changed_by,
        field_permissions: Object.entries(tableData.fields_permissions || {}).reduce(
          (acc, [fieldSlug, permissionValue]) => ({
            ...acc,
            [fieldSlug]: table
              ? getSystemTableFieldPermission(table.table_type, managePermission, permissionValue)
              : permissionValue,
          }),
          {}
        ),
      };
    });

    const payload: ApiRoleSavePayload = {
      principal_type: principalType,
      api_permissions: roleData.api_permissions,
      assign_to: isApiUserRole
        ? serviceUserEntitiesToAssignTo(roleData.api_user_entities ?? [])
        : roleData.assign_to,
      apply_to: roleData.apply_to,
      employee_field_permissions: roleData.employee_field_permissions,
      employee_custom_field_permissions: roleData.employee_custom_field_permissions,
      employee_folder_permissions: roleData.employee_folder_permissions ?? {},
      employee_workflow_permissions: roleData.employee_workflow_permissions ?? {},
      employee_custom_table_permissions: employeeCustomTablePermissions,
      title: roleData.title,
      description: roleData.description,
    };

    roleSave.mutate(
      { id: role?.id || 0, payload },
      {
        onSuccess(createdRole) {
          if (role) {
            analyticsService.trackEvent(analyticEvents.roles.edited, {
              [analyticProperties.id]: createdRole.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.roles.created, {
              [analyticProperties.id]: createdRole.id,
            });
          }

          navigate("/settings/roles-and-permissions");
        },
      }
    );
  });

  const isCustom = useMemo(() => {
    return role && role.type == RoleType.custom;
  }, [role]);

  return (
    <Page>
      <Page.Header
        showBack={true}
        title={t("core:role")}
        navigateTo={"/settings/roles-and-permissions"}
      />
      <Page.Content>
        {isLoading ? (
          <Stack gap="sm" className={"w-full"}>
            <Skeleton className={"w-1/2 h-6"}></Skeleton>
            <Skeleton className={"w-full h-5"}></Skeleton>
          </Stack>
        ) : (
          <form onSubmit={onSubmit}>
            <Stack className={"px-16"} direction={"vertical"}>
              <Stack>
                <Title
                  header={t("core:role_enter_description")}
                  size={"lg"}
                  paddingBottom
                  paddingTop
                />
                <Input
                  label={t("common:title")}
                  required={true}
                  type={"text"}
                  {...register("title", {
                    value: role?.title ?? "",
                  })}
                  readOnly={role?.type != RoleType.custom && role != undefined}
                  error={errors.title?.message}
                />
                <Textarea
                  label={t("common:description")}
                  {...register("description", {
                    value: role?.description ?? "",
                  })}
                  readOnly={role?.type != RoleType.custom && role != undefined}
                />
              </Stack>

              {isApiUserRole ? (
                <ServiceUserAssign
                  control={formHook.control}
                  name={"api_user_entities"}
                  error={formHook.formState.errors.api_user_entities?.message}
                />
              ) : (
                [RoleType.admin, RoleType.custom].indexOf(role?.type || RoleType.custom) !== -1 && (
                  <Stack direction={"vertical"} gap={"md"}>
                    <Title
                      header={t("core:assign_employees")}
                      caption={t("core:apply_to_description")}
                      size={"lg"}
                      paddingBottom
                      paddingTop
                    />

                    <RuleGroupsEditor
                      control={formHook.control}
                      setValue={formHook.setValue}
                      fieldName={"assign_to"}
                      place={RuleRegistryPlace.roleAssignTo}
                      conditionTypes={assignToConditionTypes.conditionTypes}
                      error={formHook.formState.errors.assign_to?.message}
                    />
                  </Stack>
                )
              )}

              {[RoleType.admin].indexOf(role?.type || RoleType.custom) === -1 && (
                <RoleEditTabs role={role} formHook={formHook} dataAccessActive={dataAccessActive} />
              )}

              {[RoleType.custom, RoleType.all_employees].includes(
                role?.type || RoleType.custom
              ) && (
                <Stack direction={"vertical"} gap={"md"} className="mb-4">
                  <Title
                    header={t("core:data_access_limitations")}
                    caption={t("core:data_access_limitations_description")}
                    size={"lg"}
                    paddingBottom
                    paddingTop
                  />

                  <Switch
                    size={"sm"}
                    checked={accessAllEmployees}
                    onChange={(checked) => {
                      setAccessAllEmployees(checked);
                      if (checked) {
                        formHook.setValue("apply_to", allEmployeesRuleGroups(), {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    }}
                    title={t("core:access_data_for_all_employees")}
                    description={t("core:access_data_for_all_employees_description")}
                  />

                  {!accessAllEmployees && (
                    <RuleGroupsEditor
                      control={formHook.control}
                      setValue={formHook.setValue}
                      fieldName={"apply_to"}
                      place={RuleRegistryPlace.roleApplyTo}
                      assignToGroups={assignToGroups}
                      conditionTypes={applyToConditionTypes.conditionTypes}
                      error={formHook.formState.errors.apply_to?.message}
                    />
                  )}
                </Stack>
              )}

              <Stack className={"pt-(--gap-md)"} gap={"md"}>
                <ServerErrorField errors={errors} />
                <Button
                  size={"lg"}
                  type={"submit"}
                  onClick={onSubmit}
                  isLoading={roleSave.isPending}
                >
                  {t("common:save")}
                </Button>
                <Button
                  variant={isCustom ? "danger" : "plain"}
                  size={"lg"}
                  leftIcon={isCustom ? <FontAwesomeIcon icon={"fa-light fa-xmark"} /> : undefined}
                  onClick={() => {
                    if (isCustom) {
                      handleDelete();
                    } else {
                      handleCancel();
                    }
                  }}
                >
                  {isCustom ? t("common:delete") : t("common:cancel")}
                </Button>
              </Stack>
            </Stack>
          </form>
        )}
      </Page.Content>
    </Page>
  );
}
